import React, { memo } from "react";
import {
  Dropdown,
  DropdownMenuItemType,
  getTheme,
  Icon,
  Text,
  Label,
  TextField,
  Toggle,
  IconButton,
  TooltipHost,
} from "office-ui-fabric-react";
import { useDrag } from "react-dnd";
import { ItemTypes } from "./ItemTypes";
import EditableMenuDropper from "./EditableMenuDropper";

export const EditableMenuItem = memo(function EditableMenuItem({
  item,
  updateValue,
  moveMenuItem,
  addMenuItem,
  removeMenuItem,
}) {
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: ItemTypes.MENUITEM,
      item: item,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    []
  );

  let variant = item.depth === 0 ? "large" : "medium";
  let fontWeight = item.depth === 1 ? "bold" : "normal";

  return (
    <div
      key={item.key ?? Math.floor(Math.random() * 100) + 1001}
      style={{ background: getTheme().palette.neutralLighter }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "nowrap",
          marginLeft: item.depth * 24,
          opacity: isDragging ? 0.4 : 1,
        }}
        ref={preview}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            height: 44,
            width: 220,
            marginRight: 96 - item.depth * 24,
          }}
        >
          <Icon
            iconName={
              item.icon === undefined || item.icon === ""
                ? "ChevronUp"
                : item.icon
            }
            // TODO: Collapsable menu
            style={{
              fontSize: 16,
              margin: 4,
              color: getTheme().palette.themeDarkAlt,
            }}
          />
          <Text
            variant={variant}
            style={{
              fontWeight: fontWeight,
              marginLeft: 4,
              color: getTheme().palette.neutralPrimary,
            }}
          >
            {item.name}
          </Text>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
          }}
        >
          <TextField
            label="Name"
            placeholder={item.name}
            value={item.name}
            underlined
            onChange={(e, newValue) => {
              updateValue(item.key, "name", newValue);
            }}
          />
          <TextField
            label="Url"
            placeholder={item.url}
            value={item.url}
            underlined
            onChange={(e, newValue) => {
              updateValue(item.key, "url", newValue);
            }}
          />
          <Label
            style={{
              height: 33,
              textAlign: "center",
              borderBottom: "1px solid rgb(208, 208, 208)",
              padding: "6px 12px 6px 12px",
            }}
          >
            Target
          </Label>
          <Dropdown
            selectedKey={item.target}
            placeholder="Select a target option"
            options={dropdownTargetOption}
            style={{ width: 96 }}
            onChange={(e, newItem) => {
              updateValue(item.key, "target", newItem.key);
            }}
          />
          <TextField
            label="Icon"
            placeholder={item.icon}
            value={item.icon}
            underlined
            onChange={(e, newValue) => {
              updateValue(item.key, "icon", newValue);
            }}
          />
          <div
            style={{
              display: "grid",
              placeItems: "center",
              width: 120,
              marginLeft: 12,
            }}
          >
            <Toggle
              onText="Expanded"
              offText="Collapsed"
              checked={item.isExpanded}
              onChange={(e, checked) => {
                updateValue(item.key, "isExpanded", checked);
              }}
            />
          </div>
          <div
            style={{
              display: "grid",
              placeItems: "center",
              width: 120,
              marginLeft: 12,
            }}
          >
            <Toggle
              onText="Enabled"
              offText="Disabled"
              checked={!item.disabled}
              onChange={(e, checked) => {
                updateValue(item.key, "disabled", !checked);
              }}
            />
          </div>
          <div ref={drag}>
            <Icon
              iconName="DragObject"
              style={{
                fontSize: 16,
                margin: 5,
                color: getTheme().palette.themeDarkAlt,
                cursor: "grabbing",
              }}
            />
          </div>
          <div>
            <TooltipHost content="Duplicate" id="CopyButton">
              <IconButton
                iconProps={{ iconName: "Copy" }}
                style={{
                  fontSize: 16,
                  margin: 0,
                  cursor: "pointer",
                }}
                onClick={() => {
                  addMenuItem(item.key);
                }}
              />
            </TooltipHost>
          </div>
          <div>
            <TooltipHost content="Delete" id="DeleteButton">
              <IconButton
                iconProps={{ iconName: "Delete" }}
                style={{
                  fontSize: 16,
                  margin: 0,
                  color: "red",
                  cursor: "pointer",
                }}
                onClick={() => {
                  if (
                    window.confirm(
                      `Are you sure you want to delete ${item.name}?`
                    )
                  ) {
                    removeMenuItem(item.key);
                  }
                }}
              />
            </TooltipHost>
          </div>
        </div>
      </div>
      <EditableMenuDropper
        onDrop={(editableMenuItem, monitor) => {
          if (!!monitor.isOver({ shallow: true })) {
            moveMenuItem(editableMenuItem.key, item.key);
          }
        }}
      >
        <div
          style={{
            color: getTheme().palette.themePrimary,
          }}
        >
          Move here
        </div>
      </EditableMenuDropper>
    </div>
  );
});

export default EditableMenuItem;

const dropdownTargetOption = [
  {
    key: "BasicHeader",
    text: "Basic",
    itemType: DropdownMenuItemType.Header,
  },
  { key: "frame", text: "Frame" },
  { key: "_blank", text: "Blank" },
  { key: "_self", text: "Self" },
  { key: "divider_1", text: "-", itemType: DropdownMenuItemType.Divider },
  {
    key: "AdvancedHeader",
    text: "Advanced",
    itemType: DropdownMenuItemType.Header,
  },
  { key: "_parent", text: "Parent" },
  { key: "_top", text: "Top" },
];
